import { createContext, useContext, useEffect, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "rowen_cart_v1";

function readCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(readCart);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setDrawerOpen((v) => !v), []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const keyOf = (productId, size) => `${productId}::${size || ""}`;

  const fulfillmentOf = (product) => {
    const t = product?.product_type || product?.fulfillment_type;
    return t === "in_stock" ? "in_stock" : "prebook";
  };

  const cartFulfillment = items.length > 0 ? (items[0].fulfillment_type || "prebook") : null;

  // Returns { ok: true } on success, or { ok: false, reason: "mixed" } if blocked.
  const addItem = useCallback((product, size, quantity) => {
    const ft = fulfillmentOf(product);
    let blocked = false;
    setItems((prev) => {
      const existingFt = prev.length > 0 ? (prev[0].fulfillment_type || "prebook") : null;
      if (existingFt && existingFt !== ft) {
        blocked = true;
        return prev;
      }
      const key = keyOf(product.id, size);
      const idx = prev.findIndex((i) => keyOf(i.product_id, i.size) === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          image: (product.images && product.images[0]) || "",
          size: size || "",
          quantity,
          wholesale_price: product.wholesale_price,
          msrp: product.msrp,
          moq: product.moq || 10,
          fulfillment_type: ft,
        },
      ];
    });
    return blocked ? { ok: false, reason: "mixed" } : { ok: true };
  }, []);

  // Add multiple size/quantity pairs at once (used by the order pad / quick-order grid).
  // Each pair: { size, quantity, price?, sku? }. `price` is the effective per-variant
  // wholesale price (override or product default); `sku` is the variant SKU.
  const addItems = useCallback((product, sizeQuantities) => {
    const ft = fulfillmentOf(product);
    let blocked = false;
    setItems((prev) => {
      const existingFt = prev.length > 0 ? (prev[0].fulfillment_type || "prebook") : null;
      if (existingFt && existingFt !== ft) {
        blocked = true;
        return prev;
      }
      let next = [...prev];
      sizeQuantities.forEach(({ size, quantity, price, sku }) => {
        const qty = parseInt(quantity, 10) || 0;
        if (qty <= 0) return;
        const key = keyOf(product.id, size);
        const idx = next.findIndex((i) => keyOf(i.product_id, i.size) === key);
        if (idx >= 0) {
          next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        } else {
          next.push({
            product_id: product.id,
            name: product.name,
            image: (product.images && product.images[0]) || "",
            size: size || "",
            sku: sku || product.sku || "",
            quantity: qty,
            wholesale_price: price != null ? price : product.wholesale_price,
            msrp: product.msrp,
            moq: product.moq || 10,
            fulfillment_type: ft,
          });
        }
      });
      return next;
    });
    return blocked ? { ok: false, reason: "mixed" } : { ok: true };
  }, []);

  const updateQty = useCallback((productId, size, quantity) => {
    setItems((prev) =>
      prev.map((i) =>
        keyOf(i.product_id, i.size) === keyOf(productId, size)
          ? { ...i, quantity: Math.max(0, parseInt(quantity, 10) || 0) }
          : i
      )
    );
  }, []);

  const removeItem = useCallback((productId, size) => {
    setItems((prev) => prev.filter((i) => keyOf(i.product_id, i.size) !== keyOf(productId, size)));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const subtotal = items.reduce(
    (sum, i) => sum + (i.wholesale_price || 0) * i.quantity,
    0
  );

  const totalUnits = items.reduce((sum, i) => sum + i.quantity, 0);

  // Minimum opening order: 20 units total, prebook orders only.
  // In-stock carts have no minimum.
  const MIN_ORDER_UNITS = 20;
  const isPrebookCart = items.length > 0 && (items[0].fulfillment_type || "prebook") === "prebook";
  const belowMinOrder = isPrebookCart && totalUnits < MIN_ORDER_UNITS;
  const unitsToMin = isPrebookCart ? Math.max(0, MIN_ORDER_UNITS - totalUnits) : 0;

  // Legacy per-line MOQ violations kept as no-op for compatibility with older UI.
  const moqViolations = [];

  return (
    <CartContext.Provider value={{ items, addItem, addItems, updateQty, removeItem, clearCart, subtotal, totalUnits, moqViolations, cartFulfillment, minOrderUnits: MIN_ORDER_UNITS, belowMinOrder, unitsToMin, drawerOpen, openDrawer, closeDrawer, toggleDrawer }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
